/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useEffect,
  useRef,
  useMemo,
  useLayoutEffect,
  memo,
  useContext,
} from 'react';
import { keyframes } from 'emotion';
import { css, jsx } from '@emotion/core';
import { values, colors } from 'styles/variables';
import { connect } from 'react-redux';
import getBlockById from 'services/apiHelpers/getBlockById';
import { createContext } from 'react';
import { Box, Flexbox } from '@chameleoncreator/creator-ui';
import getTopics from 'services/apiHelpers/getTopics';
import getStore from 'services/getStore';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import useImageLoader from 'hooks/useImageLoader';
import { getSuspendData } from 'services/scorm/scormService';
import isIE from 'services/isIE';
import ie11VerticalFlexCenterFix from 'services/ie11VerticalFlexCenterFix';
import { unlockInteraction } from 'services/unlockInteraction';
import { getInteractionsLockedData } from 'services/scorm/scormService';
import isLastInteraction, {
  setLastInteractionSeen,
} from 'services/apiHelpers/isLastInteraction';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import Wrapper from './Wrapper';
import { useSelector } from 'react-redux';
import HiddenBackButton from './HiddenBackButton';
import onScrollEnds from 'services/onScrollEnds';
import getAllInteractions from 'services/apiHelpers/getAllInteractions';
import Background from './Background';
import next from './next';
import updateReadOnlyText from 'services/updateReadOnlyText';
import pubSub from 'services/pubSub';
import { createSelector } from 'reselect';
import { updateInteractionsOpenAction } from 'store/next';

const store = getStore();

const { lPink } = colors;
const { headerHeight } = values;

const fadeInHilite = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

let hasFocus: boolean;

const renderCount = {};

export const InteractionContext = createContext(undefined);

const getInteractionsOpen = createSelector(
  (state: any) => state.interactionsOpen,
  interactionsOpen => interactionsOpen
);

const Container = props => {
  const {
    id,
    focusedNodeId,
    name,
    style,
    children,
    isFirstInteractionInTopic,
    // isOpen,
    lockDown,
  } = props;

  let {
    sId,
    background = undefined,
    backgroundImage = undefined,
    backgroundImageOpacity = 100,
    src = undefined,
    textColor = style.foreground,
  } = getBlockById(id).props;

  let interactionsOpen = useSelector(getInteractionsOpen);
  const isOpen = props.isOpen || interactionsOpen.includes(id);

  // dirty gotta try and cover for legacy backgroundImage
  if (src) {
    // console.log({ src, backgroundImage });
    backgroundImage = `url(${src}) center / cover no-repeat`;
  }

  const section = useRef(null);
  const selected = id === focusedNodeId;

  const parsedTextColor =
    textColor ||
    getBlockById(getCurrentTopic().id).props.textColor ||
    (store.getState().theme as any).textColor ||
    '';
  const contentContainer = useRef(null);

  useLayoutEffect(() => {
    if (!section.current) {
      section.current = document.querySelector(`#i${id}`);
    }

    !window.dev() && updateReadOnlyText(section.current);
  }, [parsedTextColor]);

  useEffect(() => {
    const pS = pubSub.subscribe('FOCUS_NODE', $ => {
      // console.log($);
      setCurrentBlock($);
    });

    return () => {
      pS();
    };
  }, []);

  useEffect(() => {
    if (selected) hasFocus = true;
    return () => {
      hasFocus = false;
    };
  }, []);

  useEffect(() => {
    if (!lockDown) {
      return;
    }

    // unlocking from saved state in lms
    if (getInteractionsLockedData(id) !== undefined) {
      unlockInteraction(id);
    }
  });

  useEffect(() => {
    if (!section.current) {
      return;
    }

    const onFocus = () => {
      // console.log(section.current);
    };

    section.current.addEventListener('focus', onFocus);

    return () => {
      section?.current.removeEventListener('focus', onFocus);
    };
  }, []);

  let isLocked = lockDown;

  // since the background gets set on the style on creation
  // kill it if we have a ' local ' background color
  // goal would be to remove it from the factory. but it's legacy from wizard
  // WINDING on down. . .
  const overideStyleBackground = props.hasOwnProperty('background')
    ? {
        background: background || '',
      }
    : {};

  const getSectionLabel = () => {
    return `Section ${getAllInteractions().findIndex((i: any) => i === id) +
      1}`;
  };

  if (!renderCount[id]) {
    renderCount[id] = 0;
  }

  // console.log(`container ${name} renders`, (renderCount[id] += 1));

  return (
    <InteractionContext.Provider
      value={{ id, next: next(sId, id), isLocked, children }}
    >
      <Flexbox
        as="section"
        id={`i${id}`}
        css={css`
          ${!isOpen &&
            `
          display: none;
      `}

          .chameleon-text {
            ${parsedTextColor && `color: ${parsedTextColor};`}
          }
        `}
        style={{
          position: 'relative',
          overflowX: 'hidden',
          ...{
            ...style,
            ...overideStyleBackground,
          },
        }}
        py={!isIE() ? 4 : 0}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight={'100vh'}
        width={['100%']}
        paddingTop={70}
        data-section
        aria-label={getSectionLabel() || name || props['aria-label']}
        role="main"
        tabIndex={-1}
      >
        <Background
          // key={background && background.includes('url') && background}
          // background={background}
          key={backgroundImage}
          background={backgroundImage}
          backgroundImageOpacity={backgroundImageOpacity}
        />

        <HiddenBackButton id={id} />

        {!isIE() ? (
          <Flexbox
            ref={contentContainer}
            position="relative"
            flexDirection="column"
            justifyContent="center"
            // height="auto"
            width={['100%']}
            maxWidth={['100%', null, '1100px']}
          >
            {children}
          </Flexbox>
        ) : (
          <div
            ref={contentContainer}
            css={css`
              position: relative;
              display: flex;
              // flex-direction: column;
              justify-content: center;
              align-items: center;
              width: 100%;
              max-width: 1100px;
              min-height: calc(100vh - 70px);

              ${ie11VerticalFlexCenterFix(
                `height: ${
                  window.dev()
                    ? `calc(100% - ${headerHeight}px)`
                    : 'calc(100vh - 70px)'
                }`
              )}
            `}
          >
            <div
              css={css`
                position: relative;
                display: flex;
                flex-direction: column;
                width: 100%;
              `}
            >
              {children}
            </div>
          </div>
        )}

        {window.dev() && <Wrapper id={id} focusedNodeId={focusedNodeId} />}
      </Flexbox>
    </InteractionContext.Provider>
  );
};

// export default Container;

export default connect(
  ({ focusedNodeId }: { focusedNodeId: string | number }) => ({
    focusedNodeId,
    // forceUpdate: Math.random(),
  })
)(Container);
